import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getTokenRole } from '../utils/api';
import { fetchThroughSleep, isGatewayStatus } from '../utils/wakeBackend';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, onTeamSettingsUpdate, fetchTeamSettings }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  // Superadmin club impersonation
  const [impersonatedClubCode, setImpersonatedClubCode] = useState(localStorage.getItem('impersonatedClubCode'));
  const [statusMessage, setStatusMessage] = useState('');

  const trackWake = useCallback((state) => {
    setStatusMessage(state === 'restarting'
      ? 'The server did not wake. Starting it back up…'
      : 'The server is asleep. Waking it…');
  }, []);

  // Handle team settings update
  const handleTeamSettings = useCallback((teamSettings) => {
    if (teamSettings && onTeamSettingsUpdate) {
      onTeamSettingsUpdate(teamSettings);
    }
  }, [onTeamSettingsUpdate]);

  // Load full team settings (including feature flags) from /api/teams/settings.
  const loadTeamSettings = useCallback(async (authToken, fallbackSettings = null) => {
    if (fetchTeamSettings && authToken) {
      try {
        const settings = await fetchTeamSettings(authToken);
        if (settings) return;
      } catch (err) {
        console.error('[AuthContext] Failed to fetch team settings:', err);
      }
    }
    if (fallbackSettings) {
      handleTeamSettings(fallbackSettings);
    }
  }, [fetchTeamSettings, handleTeamSettings]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    // Re-read token from localStorage in case state is stale
    const currentToken = localStorage.getItem('token');
    const storedImpersonatedClub = localStorage.getItem('impersonatedClubCode');
    
    console.log('[AuthContext] verifyToken called', {
      hasToken: !!currentToken,
      impersonatedClub: storedImpersonatedClub
    });
    
    if (!currentToken) {
      console.log('[AuthContext] No token found, skipping verification');
      setLoading(false);
      return;
    }
    
    try {
      const headers = {
        'Authorization': `Bearer ${currentToken}`
      };

      const tokenRole = getTokenRole(currentToken);

      // Only superadmins may impersonate. Drop a leftover localStorage value
      // so a shared browser never sends X-Club-Code for a regular account.
      if (storedImpersonatedClub && tokenRole === 'superadmin') {
        headers['X-Club-Code'] = storedImpersonatedClub;
        console.log('[AuthContext] Adding X-Club-Code header:', storedImpersonatedClub);
      } else if (storedImpersonatedClub) {
        localStorage.removeItem('impersonatedClubCode');
      }
      
      const response = await fetchThroughSleep(`${API_URL}/api/auth/verify`, { headers }, {
        onStatus: trackWake
      });
      console.log('[AuthContext] verify response status:', response?.status);

      if (response?.ok) {
        const data = await response.json();
        console.log('[AuthContext] verify success, user role:', data.user?.role);
        setUser(data.user);
        
        if (data.user?.role === 'superadmin' && storedImpersonatedClub) {
          console.log('[AuthContext] Superadmin impersonating:', storedImpersonatedClub);
          setImpersonatedClubCode(storedImpersonatedClub);
        } else {
          localStorage.removeItem('impersonatedClubCode');
          setImpersonatedClubCode(null);
        }
        if (data.token) {
          localStorage.setItem('token', data.token);
          setToken(data.token);
        }
        await loadTeamSettings(data.token || currentToken, data.teamSettings);
      } else if (response && isGatewayStatus(response.status)) {
        // The replica is still asleep. Keep the saved session and let the
        // next visit, or a login, try the wake again.
        console.warn('[AuthContext] verify hit a sleeping server:', response.status);
      } else {
        const errorText = response ? await response.text() : '';
        console.error('[AuthContext] verify failed:', response?.status, errorText);
        localStorage.removeItem('token');
        localStorage.removeItem('impersonatedClubCode');
        setToken(null);
        setUser(null);
        setImpersonatedClubCode(null);
      }
    } catch (error) {
      console.error('[AuthContext] Token verification failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('impersonatedClubCode');
      setToken(null);
      setUser(null);
      setImpersonatedClubCode(null);
    } finally {
      setStatusMessage('');
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetchThroughSleep(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      }, { onStatus: trackWake });

      if (!response || isGatewayStatus(response.status)) {
        return { success: false, error: 'The server is still starting. Try again in a minute.' };
      }

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        await loadTeamSettings(data.token, data.teamSettings);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    } finally {
      setStatusMessage('');
    }
  };

  const register = async (email, password, name, acceptedTerms = false, acceptedEULA = false, inviteToken = null) => {
    try {
      // Invitation-based registration: all club/team/role info comes from the invitation
      const requestBody = { email, password, name, acceptedTerms, acceptedEULA, inviteToken };
      console.log('[AuthContext] Register request body:', { ...requestBody, password: '***' });
      const response = await fetchThroughSleep(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }, { onStatus: trackWake });

      if (!response || isGatewayStatus(response.status)) {
        return { success: false, error: 'The server is still starting. Try again in a minute.' };
      }

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        await loadTeamSettings(data.token, data.teamSettings);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error' };
    } finally {
      setStatusMessage('');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('teamSettings');
    localStorage.removeItem('impersonatedClubCode');
    setToken(null);
    setUser(null);
    setImpersonatedClubCode(null);
  };

  const isCoach = () => {
    return user && (user.role === 'coach' || user.role === 'admin' || user.role === 'superadmin');
  };

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'superadmin');
  };

  const isSuperAdmin = () => {
    return user && user.role === 'superadmin';
  };

  const isCoxswain = () => {
    return user && user.role === 'coxswain';
  };

  const canEnterTeamResults = () => {
    return isCoach() || isCoxswain();
  };

  const canMarkTeamAttendance = () => {
    return isCoach() || isCoxswain();
  };

  // Get headers for API calls, including impersonation header if needed
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    // If superadmin is impersonating a club, add the header
    if (user?.role === 'superadmin' && impersonatedClubCode) {
      headers['X-Club-Code'] = impersonatedClubCode;
    }
    return headers;
  };

  // Switch club (superadmin only)
  const switchClub = async (newClubCode) => {
    if (user?.role !== 'superadmin') {
      return { success: false, error: 'Only superadmins can switch clubs' };
    }
    
    if (newClubCode) {
      localStorage.setItem('impersonatedClubCode', newClubCode);
      setImpersonatedClubCode(newClubCode);
      
      // Fetch and update team settings for the new club
      try {
        const response = await fetch(`${API_URL}/api/teams/public/${newClubCode}`);
        if (response.ok) {
          const data = await response.json();
          onTeamSettingsUpdate?.(data.settings);
        }
      } catch (error) {
        console.error('Error fetching team settings for impersonated club:', error);
      }
    } else {
      // Clear impersonation - go back to own club
      localStorage.removeItem('impersonatedClubCode');
      setImpersonatedClubCode(null);
      // Refresh to get own team settings
      if (token) {
        verifyToken();
      }
    }
    
    return { success: true };
  };

  // Get current effective club code (impersonated or own)
  const getEffectiveClubCode = () => {
    if (user?.role === 'superadmin' && impersonatedClubCode) {
      return impersonatedClubCode;
    }
    return user?.clubCode;
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          await loadTeamSettings(token, data.teamSettings);
          return { success: true };
        } else {
          return { success: false, error: 'Failed to refresh user data' };
        }
      } catch (error) {
        console.error('Refresh user error:', error);
        return { success: false, error: 'Network error' };
      }
    }
    return { success: false, error: 'No token available' };
  };

  const switchActiveTeam = async (team) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      };
      if (user?.role === 'superadmin' && impersonatedClubCode) {
        headers['X-Club-Code'] = impersonatedClubCode;
      }

      const response = await fetch(`${API_URL}/api/auth/active-team`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ team })
      });
      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        await loadTeamSettings(data.token, data.teamSettings);
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to switch team' };
    } catch (error) {
      console.error('[AuthContext] switchActiveTeam error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const switchActiveClub = async (clubCode) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      };
      if (user?.role === 'superadmin' && impersonatedClubCode) {
        headers['X-Club-Code'] = impersonatedClubCode;
      }

      const response = await fetch(`${API_URL}/api/auth/active-club`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ clubCode })
      });
      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        await loadTeamSettings(data.token, data.teamSettings);
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to switch club' };
    } catch (error) {
      console.error('[AuthContext] switchActiveClub error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const leaveClub = async (clubCode) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      };
      const response = await fetch(`${API_URL}/api/auth/club-memberships`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ clubCode })
      });
      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        await loadTeamSettings(data.token, data.teamSettings);
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to leave club' };
    } catch (error) {
      console.error('[AuthContext] leaveClub error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const value = {
    user,
    token,
    loading,
    statusMessage,
    login,
    register,
    logout,
    isCoach,
    isAdmin,
    isSuperAdmin,
    isCoxswain,
    canEnterTeamResults,
    canMarkTeamAttendance,
    refreshUser,
    switchActiveTeam,
    switchActiveClub,
    leaveClub,
    // Superadmin features
    impersonatedClubCode,
    switchClub,
    getEffectiveClubCode,
    getAuthHeaders
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
